import { PropsWithChildren, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogOverlay,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  PasswordInput,
  Button,
} from '@leadsnavi/ui-kit';
import { useForm } from 'react-hook-form';
import { X } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import useUserProfile from '@/stores/user-profile';
import useUserStore from '@/stores/userStore';
import { changePasswordPost } from '@/services/swagger/easesourcing-auth/UserAuthService';
import { PasswordSchema, PasswordDetail } from '../dto/password.dto';

export default function ChangePassword(props: PropsWithChildren) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { closeDialog } = useUserProfile();
  const { cleanupAuth } = useUserStore();

  const form = useForm<PasswordDetail>({
    resolver: zodResolver(PasswordSchema),
    mode: 'onBlur',
    defaultValues: {
      password: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    shouldFocusError: true, // 自动聚焦到第一个错误字段
    // criteriaMode: 'firstError', // 或 "all"
  });

  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open);
      if (!open) {
        form.reset(); // 关闭时重置表单
      }
    },
    [form]
  );

  const onSubmit = useCallback(
    async (data: PasswordDetail) => {
      console.log('Form Data:', data);
      try {
        setLoading(true);
        await changePasswordPost({
          oldPassword: data.password,
          newPassword: data.newPassword,
        });
        // 提交成功后的逻辑处理
        onOpenChange(false); // 关闭对话框
        cleanupAuth();
        // 关闭个人中心弹窗
        closeDialog();
        // 刷新页面
        window.location.href = '/';
      } finally {
        setLoading(false);
      }
    },
    [onOpenChange, closeDialog, cleanupAuth]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className="hidden" />
      <DialogTrigger asChild>{props.children}</DialogTrigger>
      <DialogContent className="w-[450px]" showCloseButton={false}>
        <DialogTitle className="flex items-center justify-between">
          <div>Change password</div>
          <DialogClose>
            <X className="cursor-pointer" />
          </DialogClose>
        </DialogTitle>
        <div className="py-4">
          <Form {...form}>
            <form className="space-y-6" autoComplete="off">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <PasswordInput size="lg" allowClear placeholder="Enter the current password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        size="lg"
                        allowClear
                        placeholder="8–20 characters, at least 1 number and 1 letter"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPasswordConfirm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Re-enter the password</FormLabel>
                    <FormControl>
                      <PasswordInput size="lg" allowClear placeholder="Enter the new password again" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <div className="flex justify-end space-x-2">
          <DialogClose>
            <Button variant="outline" size="lg">
              Cancel
            </Button>
          </DialogClose>

          <Button size="lg" onClick={form.handleSubmit(onSubmit)} loading={loading}>
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
